/**
 * Translate an undo description (an English literal built by an operation).
 *
 * Pure: the translator is passed in. Descriptions with a count or a name in
 * them ("Completed 3 tasks", "Plan: Morning", "Create task: <title>") map to a
 * key with `{{n}}` / `{{name}}`. The variable is passed as an interpolation
 * value, never as a default value, so user text such as a task title that
 * contains `{{x}}` is not interpolated; with no translation the original
 * description is returned untouched.
 */

export interface UndoTranslator {
  exists: (key: string) => boolean
  t: (key: string, options?: Record<string, unknown>) => string
}

const DYNAMIC_DESCRIPTIONS: Array<{ re: RegExp; key: (m: RegExpExecArray) => string }> = [
  {
    re: /^(Completed|Deleted|Uncompleted|Scheduled|Canceled|Restored|Moved|Set deadline on|Cleared deadline on) (\d+) tasks$/,
    key: (m) => `${m[1]} {{n}} tasks`,
  },
  { re: /^Moved (\d+) tasks to trash$/, key: () => "Moved {{n}} tasks to trash" },
  { re: /^Reopened (\d+) to-dos$/, key: () => "Reopened {{n}} to-dos" },
  { re: /^Plan: (.+)$/s, key: () => "Plan: {{name}}" },
  { re: /^Create task: (.+)$/s, key: () => "Create task: {{name}}" },
]

export function translateUndoDescription(
  description: string,
  translator: UndoTranslator,
): string {
  if (translator.exists(description)) return translator.t(description)
  for (const { re, key } of DYNAMIC_DESCRIPTIONS) {
    const m = re.exec(description)
    if (!m) continue
    const k = key(m)
    if (!translator.exists(k)) return description
    const variable = m[m.length - 1] ?? ""
    return translator.t(k, { n: variable, name: variable })
  }
  return description
}
